import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Partner role type mapping for Recharge Sales Insights APIs.
 * Maps partner role (e.g. ZD, ARD) and section (PRIMARY, SECONDARY, TERTIARY, SELL_OUT)
 * to partner_role_types array for pams-insights API payloads.
 *
 * Table reference:
 * PARTNER ROLE | PRIMARY | SECONDARY | TERTIARY    | SELL OUT
 * ZD           | 754     | 755       | [104,110]   | [104,110]
 * ARD          | 752     | NA        | [101,123]   | [101,123]
 * DT           | 756     | NA        | NA          | NA
 * END          | END     | NA        | NA          | [END,EDF]
 * ITD          | NA      | ITD       | [ITR,IMB,DMB] | [ITR,IMB,DMB]
 * ITZ          | ITZ     | ITD       | [ITR,IMB,DMB] | [ITR,IMB,DMB]
 * MARD         | 782     | NA        | [101,123]   | [101,123]
 * RDS          | NA      | 755       | [104,110]   | [104,110]
 * RZD          | RZD     | RZD       | DRS         | DRS
 * ZDMT         | 757     | CHAIN     | NA          | 106
 * ZDMTC        | NA      | CHAIN     | NA          | 106
 */

const toArray = (value) => {
  if (value === null || value === undefined || value === "NA") return [];
  if (Array.isArray(value)) return value.map((v) => String(v));
  return [String(value)];
};

const PARTNER_ROLE_SECTION_MAP = {
  ZD: {
    PRIMARY: ["754"],
    SECONDARY: ["755"],
    TERTIARY: ["104", "110"],
    SELL_OUT: ["104", "110"],
  },
  ARD: {
    PRIMARY: ["752"],
    SECONDARY: [],
    TERTIARY: ["101", "123"],
    SELL_OUT: ["101", "123"],
  },
  DT: {
    PRIMARY: ["756"],
    SECONDARY: [],
    TERTIARY: [],
    SELL_OUT: [],
  },
  END: {
    PRIMARY: ["END"],
    SECONDARY: [],
    TERTIARY: [],
    SELL_OUT: ["END", "EDF"],
  },
  ITD: {
    PRIMARY: [],
    SECONDARY: ["ITD"],
    TERTIARY: ["ITR", "IMB", "DMB"],
    SELL_OUT: ["ITR", "IMB", "DMB"],
  },
  ITZ: {
    PRIMARY: ["ITZ"],
    SECONDARY: ["ITD"],
    TERTIARY: ["ITR", "IMB", "DMB"],
    SELL_OUT: ["ITR", "IMB", "DMB"],
  },
  MARD: {
    PRIMARY: ["782"],
    SECONDARY: [],
    TERTIARY: ["101", "123"],
    SELL_OUT: ["101", "123"],
  },
  RDS: {
    PRIMARY: [],
    SECONDARY: ["755"],
    TERTIARY: ["104", "110"],
    SELL_OUT: ["104", "110"],
  },
  RZD: {
    PRIMARY: ["RZD"],
    SECONDARY: ["RZD"],
    TERTIARY: ["DRS"],
    SELL_OUT: ["DRS"],
  },
  ZDMT: {
    PRIMARY: ["757"],
    SECONDARY: ["CHAIN"],
    TERTIARY: [],
    SELL_OUT: ["106"],
  },
  ZDMTC: {
    PRIMARY: [],
    SECONDARY: ["CHAIN"],
    TERTIARY: [],
    SELL_OUT: ["106"],
  },
};

/**
 * Returns partner_role_types array for the given partner role and section.
 * @param {string} partnerRoleName - e.g. "ZD", "ARD", "DT"
 * @param {string} section - One of "PRIMARY" | "SECONDARY" | "TERTIARY" | "SELL_OUT"
 * @returns {string[]}
 */
export function getPartnerRoleTypesForInsights(partnerRoleName, section) {
  if (!partnerRoleName || !section) return [];
  const normalizedPartner = String(partnerRoleName).toUpperCase().trim();
  let normalizedSection = String(section).toUpperCase().replace(/\s+/g, "_");
  if (normalizedSection === "SELLOUT") normalizedSection = "SELL_OUT";
  const map = PARTNER_ROLE_SECTION_MAP[normalizedPartner];
  if (!map) return [];
  return toArray(map[normalizedSection]);
}

export const INSIGHTS_SECTIONS = [
  { key: "PRIMARY", label: "Primary" },
  { key: "SECONDARY", label: "Secondary" },
  { key: "TERTIARY", label: "Tertiary" },
  { key: "SELL_OUT", label: "Sell Out" },
];

export const INSIGHTS_METRIC_TYPES = [
  {
    key: "daily",
    label: "Daily Trends",
    subtitle: "Last 30 days",
    days: 30,
  },
  {
    key: "weekly",
    label: "Weekly Trends",
    subtitle: "Last 3 months",
    weeks: 12,
  },
  {
    key: "monthly",
    label: "Monthly Trends",
    subtitle: "Last 3 months",
    months: 3,
  },
  { key: "mtdLmtd", label: "MTD / LMTD" },
];

/**
 * Maps Recharge Sales tile name (from backend) to insights section key.
 * Used to load insights dynamically per tile like other sales data.
 */
const TILE_NAME_TO_SECTION = {
  Primary: "PRIMARY",
  "Primary sales": "PRIMARY",
  "Primary Sales": "PRIMARY",
  Secondary: "SECONDARY",
  "Secondary sales": "SECONDARY",
  "Secondary Sales": "SECONDARY",
  Secondry: "SECONDARY",
  "Secondry sales": "SECONDARY",
  "Secondry sales (MT Chain)": "SECONDARY",
  Tertiary: "TERTIARY",
  "Tertiary sales": "TERTIARY",
  "Tertiary Sales": "TERTIARY",
  "Sell out": "SELL_OUT",
  "Sell Out": "SELL_OUT",
  "Sell Out sales": "SELL_OUT",
};

const SECTION_ORDER = ["PRIMARY", "SECONDARY", "TERTIARY", "SELL_OUT"];

export function getSectionKeyFromTileName(tileName) {
  if (!tileName) return null;
  const normalized = String(tileName).trim();
  const exact =
    TILE_NAME_TO_SECTION[normalized] ||
    TILE_NAME_TO_SECTION[normalized.toLowerCase()];
  if (exact) return exact;
  const lower = normalized.toLowerCase();
  if (lower.startsWith("primary")) return "PRIMARY";
  if (lower.startsWith("second")) return "SECONDARY";
  if (lower.startsWith("tertiary")) return "TERTIARY";
  if (lower.startsWith("sell")) return "SELL_OUT";
  return null;
}

/** Section key by tile index (0 = Primary, 1 = Secondary, 2 = Tertiary, 3 = Sell Out). */
export function getSectionKeyFromTileIndex(tileIndex) {
  if (tileIndex == null || tileIndex < 0 || tileIndex >= SECTION_ORDER.length)
    return null;
  return SECTION_ORDER[tileIndex];
}

/**
 * Parses insights API response into labels and values for a line chart.
 * Handles: { trends: [...] }, { content: { trends: [...] } }, or array of { date/period/label, value/count/amount }.
 * @param {*} response - Raw API response (object or array)
 * @returns {{ labels: string[], values: number[] }}
 */
export function parseInsightsTrends(response) {
  const labels = [];
  const values = [];
  let list = null;

  if (response == null) return { labels, values };

  if (Array.isArray(response)) {
    list = response;
  } else if (Array.isArray(response.trends)) {
    list = response.trends;
  } else if (response.content && Array.isArray(response.content.trends)) {
    list = response.content.trends;
  } else if (typeof response === "object" && response.trends) {
    list = Array.isArray(response.trends) ? response.trends : [];
  }

  if (!list || !list.length) return { labels, values };

  const valueKeys = [
    "value",
    "count",
    "amount",
    "quantity",
    "total",
    "revenue",
    "sales",
  ];
  const labelKeys = [
    "date",
    "period",
    "label",
    "day",
    "week",
    "month",
    "name",
    "key",
  ];

  list.forEach((item, index) => {
    if (item == null) return;
    if (typeof item === "number" && !Number.isNaN(item)) {
      labels.push(String(index + 1));
      values.push(item);
      return;
    }
    if (typeof item === "object") {
      const label = labelKeys
        .map((k) => item[k])
        .find((v) => v != null && v !== "");
      const value = valueKeys
        .map((k) => item[k])
        .find((v) => v != null && v !== "");
      labels.push(label != null ? String(label) : `Period ${index + 1}`);
      values.push(
        typeof value === "number" && !Number.isNaN(value)
          ? value
          : Number(value) || 0,
      );
      return;
    }
    labels.push(String(index + 1));
    values.push(Number(item) || 0);
  });

  return { labels, values };
}

/** Get array from API response (content or direct array). */
function getInsightsArray(response) {
  if (response == null) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.content)) return response.content;
  if (Array.isArray(response.trends)) return response.trends;
  if (Array.isArray(response.mtd_lmtd_points)) return response.mtd_lmtd_points;
  if (Array.isArray(response.data)) return response.data;
  if (response.data != null && Array.isArray(response.data.content)) {
    return response.data.content;
  }
  return [];
}

/**
 * Recharge PAMS uses `agg_actual_value`; SIM activation (sim-act-metrics) uses `agg_qty` and similar.
 */
function pickInsightsMetricValue(item) {
  if (item == null || typeof item !== "object") return NaN;
  const raw =
    item.agg_actual_value ??
    item.agg_qty ??
    item.quantity ??
    item.count ??
    item.value ??
    item.metric_value ??
    item.metricValue;
  const n = Number(raw);
  return Number.isNaN(n) ? NaN : n;
}

function pickDailyLabel(item) {
  if (item == null || typeof item !== "object") return "";
  if (item.rh_date != null) return String(item.rh_date);
  if (item.date != null) return String(item.date);
  if (item.period != null) return String(item.period);
  return "";
}

function pickWeeklyLabel(item) {
  if (item == null || typeof item !== "object") return "";
  if (item.week_start != null) return String(item.week_start);
  if (item.week != null) return String(item.week);
  if (item.week_end != null) return String(item.week_end);
  return "";
}

function pickMonthlyLabel(item) {
  if (item == null || typeof item !== "object") return "";
  if (item.month_start != null) return String(item.month_start);
  if (item.month != null) return String(item.month);
  if (item.sales_type != null) return String(item.sales_type);
  return "";
}

/** DD-MM-YYYY etc. → dayjs (PAMS / sim-act-metrics). */
function parseInsightsDateString(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const formats = [
    "DD-MM-YYYY",
    "D-M-YYYY",
    "DD/MM/YYYY",
    "D/M/YYYY",
    "YYYY-MM-DD",
  ];
  for (const fmt of formats) {
    const d = dayjs(s, fmt, true);
    if (d.isValid()) return d;
  }
  const loose = dayjs(s);
  return loose.isValid() ? loose : null;
}

/** Pie legend only: 01-02-2026 → Feb/2026 (data.labels stay raw dates for tooltips). */
export function formatInsightMonthYearSlash(raw) {
  if (raw == null || raw === "") return "";
  const d = parseInsightsDateString(raw);
  if (d) return d.format("MMM/YYYY");
  return String(raw);
}

function pickMtdLmtdValues(item) {
  if (item == null || typeof item !== "object") {
    return { mtd: 0, lmtd: 0 };
  }
  /** Recharge: mtd_actual_value; SIM sim-act-metrics: mtd_agg_qty / lmtd_agg_qty */
  const mtdRaw =
    item.mtd_actual_value ??
    item.mtd_agg_qty ??
    item.mtd_qty ??
    item.mtd_value ??
    item.mtd;
  const lmtdRaw =
    item.lmtd_actual_value ??
    item.lmtd_agg_qty ??
    item.lmtd_qty ??
    item.lmtd_value ??
    item.lmtd;
  return {
    mtd: Number(mtdRaw) || 0,
    lmtd: Number(lmtdRaw) || 0,
  };
}

function pickMtdLmtdLabel(item) {
  if (item == null || typeof item !== "object") return "";
  if (item.day_of_month != null) return String(item.day_of_month);
  if (item.mtd_date != null) return String(item.mtd_date);
  if (item.lmtd_date != null) return String(item.lmtd_date);
  if (item.day != null) return String(item.day);
  return "";
}

/**
 * Daily trends: [{ rh_date, agg_actual_value | agg_qty }] → line chart.
 */
export function parseDailyTrends(response) {
  const list = getInsightsArray(response);
  const labels = [];
  const values = [];
  list.forEach((item) => {
    if (item == null) return;
    const label = pickDailyLabel(item);
    const value = pickInsightsMetricValue(item);
    labels.push(label);
    values.push(Number.isNaN(value) ? 0 : value);
  });
  return { labels, values };
}

/**
 * Weekly trends: [{ week_start, agg_actual_value | agg_qty }] → bar chart.
 */
export function parseWeeklyTrends(response) {
  const list = getInsightsArray(response);
  const labels = [];
  const values = [];
  list.forEach((item) => {
    if (item == null) return;
    const label = pickWeeklyLabel(item);
    const value = pickInsightsMetricValue(item);
    labels.push(label);
    values.push(Number.isNaN(value) ? 0 : value);
  });
  return { labels, values };
}

/**
 * Monthly trends: [{ month_start, agg_actual_value | agg_qty }] → pie chart.
 */
export function parseMonthlyTrends(response) {
  const list = getInsightsArray(response);
  const labels = [];
  const values = [];
  list.forEach((item) => {
    if (item == null) return;
    const label = pickMonthlyLabel(item);
    const value = pickInsightsMetricValue(item);
    labels.push(label);
    values.push(Number.isNaN(value) ? 0 : value);
  });
  return { labels, values };
}

/**
 * MTD/LMTD trends: supports mtd_actual_value / mtd_qty and lmtd_* variants.
 */
export function parseMtdLmtdTrends(response) {
  const list = getInsightsArray(response);
  const labels = [];
  const mtdValues = [];
  const lmtdValues = [];
  list.forEach((item) => {
    if (item == null) return;
    const label = pickMtdLmtdLabel(item);
    const { mtd, lmtd } = pickMtdLmtdValues(item);
    labels.push(label);
    mtdValues.push(mtd);
    lmtdValues.push(lmtd);
  });
  return { labels, mtdValues, lmtdValues };
}
